// frontend/src/components/OrdersPage/OrderFinances/OrderFinancesPage.jsx

import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { authFetch } from "../../api/api";
import Button from "../../UI/Button";
import Modal from "../../UI/Modal";
import OrderFinanceProofUpload from "./OrderFinanceProofUpload";
import { useTranslation } from "react-i18next";
import OrderPaymentInfographic from "./OrderPaymentInfographic";
import ClientPaymentAllocationModal from "./ClientPaymentAllocationModal";
import OurPaymentToFactoryModal from "./OurPaymentToFactoryModal";
import CommissionPaymentRow from "./components/CommissionPaymentRow";
import ActionsMenu, { MenuItem, MenuDivider } from "./components/ActionsMenu";
import AddFactoryPaymentMenu from "./components/AddFactoryPaymentMenu";
import CompanyAccountPaymentModal from "./components/CompanyAccountPaymentModal";

import {
  BASE_URL,
  ORDER_FINANCES_API,
  ORDER_API_URL,
  ACCOUNTING_API,
  SUPPLIER_PREPAYMENTS_API,
  COMMISSION_ALLOCATIONS_API,
  PROFIT_ALLOCATIONS_API,
  EMPTY_FACTORY_FORM,
  FACTORY_FIELDS,
  PAYMENT_STATUS_OPTIONS,
} from "./constants";

import {
  getList,
  getErrorMessage,
  formatDate,
  formatMoney,
  formatPaymentStatus,
  getDocumentUrl,
} from "./utils";

export default function OrderFinancesPage() {
  const { id } = useParams();
  const { t } = useTranslation("OrdersPage");
  const navigate = useNavigate();

  // Старый API используется только для наших платежей фабрике.
  const [finances, setFinances] = useState([]);

  // Клиентские поступления берутся только из Accounting.
  const [accountingRecords, setAccountingRecords] = useState([]);
  const [supplierIncomes, setSupplierIncomes] = useState([]);
  const [supplierIncomesError, setSupplierIncomesError] = useState("");

  // Комиссионные вознаграждения по этому заказу.
  const [commissionPayments, setCommissionPayments] = useState([]);
  const [commissionsLoading, setCommissionsLoading] = useState(false);
  const [commissionsError, setCommissionsError] = useState("");

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accountingError, setAccountingError] = useState("");

  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [selectedFinance, setSelectedFinance] = useState(null);
  const [factoryForm, setFactoryForm] = useState(EMPTY_FACTORY_FORM);
  const [saving, setSaving] = useState(false);

  const [proofGalleryFinance, setProofGalleryFinance] = useState(null);
  const [confirmDeleteFinance, setConfirmDeleteFinance] = useState(null);
  const [previewProof, setPreviewProof] = useState(null);
  const [confirmDeleteProof, setConfirmDeleteProof] = useState(null);

  const [proofType, setProofType] = useState("factory");

  const [showContractModal, setShowContractModal] = useState(false);
  const [contractDate, setContractDate] = useState("");
  const [contractLoading, setContractLoading] = useState(false);

  const [accountingPaymentToDelete, setAccountingPaymentToDelete] =
    useState(null);

  const [clientEditOpen, setClientEditOpen] = useState(false);
  const [clientAllocationModalOpen, setClientAllocationModalOpen] =
    useState(false);

  const [ourEditOpen, setOurEditOpen] = useState(false);
  const [ourEditPayment, setOurEditPayment] = useState(null);
  const [ourEditForm, setOurEditForm] = useState({
    order: "",
    amount: "",
    date: "",
    paymentAgent: "",
    paymentStatus: "",
    comment: "",
  });
  const [ourEditFiles, setOurEditFiles] = useState([]);
  const [ourEditError, setOurEditError] = useState("");

  const [clientEditPayment, setClientEditPayment] = useState(null);
  const [clientEditForm, setClientEditForm] = useState({
    order: "",
    amount: "",
    date: "",
    paymentAgent: "",
    paymentStatus: "",
    comment: "",
  });
  const [clientEditFiles, setClientEditFiles] = useState([]);
  const [clientEditError, setClientEditError] = useState("");

  const [ourFactoryModalOpen, setOurFactoryModalOpen] = useState(false);


  const [companyAccountModalOpen, setCompanyAccountModalOpen] = useState(false);
  const [companyPayments, setCompanyPayments] = useState([]);
  const [companyPaymentsLoading, setCompanyPaymentsLoading] = useState(false);
  const [companyPaymentsError, setCompanyPaymentsError] = useState("");


  const [companyPaymentToEdit, setCompanyPaymentToEdit] = useState(null);
  const [companyPaymentToDelete, setCompanyPaymentToDelete] = useState(null);
  const [companyPaymentDeleting, setCompanyPaymentDeleting] = useState(false);
  const [companyPaymentDeleteError, setCompanyPaymentDeleteError] = useState("");

  // ---------------------------------------------------------
  // NAVIGATION
  // ---------------------------------------------------------

  const openPaymentDetails = (payment) => {
    if (payment.type === "client") {
      navigate(`/accounting/${payment.recordId}/`);
      return;
    }

    if (payment.type === "our") {
      navigate(
        `/accounting/${payment.recordId}/allocations/${payment.allocationId}/`
      );
      return;
    }

    if (payment.type === "supplier_income") {
      navigate(`/accounting/supplier-accounts/${payment.supplierId}`);
      return;
    }

    const finance = finances.find(
      (item) => String(item.id) === String(payment.id)
    );
    if (finance) {
      openEditModal(finance);
    }
  };

  // ---------------------------------------------------------
  // CLIENT EDIT
  // ---------------------------------------------------------

  const editAccountingPayment = async (payment) => {
    if (payment.type !== "client") return;

    setSaving(true);
    setClientEditError("");

    try {
      const response = await authFetch(
        `${ACCOUNTING_API}${payment.recordId}/`
      );

      if (!response.ok) {
        throw new Error(
          await getErrorMessage(response, "Не удалось загрузить платёж")
        );
      }

      const record = await response.json();

      const allocation = (record.allocations || []).find(
        (item) => Number(item.id) === Number(payment.allocationId)
      );

      if (!allocation) {
        throw new Error("Клиентское распределение не найдено");
      }

      setClientEditPayment({
        ...payment,
        record,
        allocation,
      });

      setClientEditForm({
        order: String(allocation.order?.id ?? allocation.order ?? ""),
        amount: String(allocation.amount ?? ""),
        date: allocation.date || record.date || "",
        paymentAgent: allocation.payment_agent || "",
        paymentStatus: allocation.payment_status || "",
        comment: allocation.comment ?? record.comment ?? "",
      });

      setClientEditFiles([]);
      setClientEditOpen(true);
    } catch (error) {
      console.error(error);
      alert(error.message || "Не удалось открыть редактирование");
    } finally {
      setSaving(false);
    }
  };

  const saveClientAccountingPayment = async (event) => {
    event.preventDefault();

    if (!clientEditPayment) return;

    const amount = Number(clientEditForm.amount);

    if (!Number.isFinite(amount) || amount <= 0) {
      setClientEditError("Сумма должна быть больше нуля");
      return;
    }

    setSaving(true);
    setClientEditError("");

    try {
      const { record, allocation } = clientEditPayment;
      const detailUrl = `${ACCOUNTING_API}${record.id}/`;

      const detailResponse = await authFetch(detailUrl);

      if (!detailResponse.ok) {
        throw new Error(
          await getErrorMessage(
            detailResponse,
            "Не удалось загрузить актуальную запись"
          )
        );
      }

      const latestRecord = await detailResponse.json();
      const allocations = Array.isArray(latestRecord.allocations)
        ? latestRecord.allocations
        : [];

      const selectedAllocation = allocations.find(
        (item) => Number(item.id) === Number(allocation.id)
      );

      if (!selectedAllocation) {
        throw new Error("Клиентское распределение больше не существует");
      }

      const updatedAllocations = allocations.map((item) => {
        const orderId = item.order?.id ?? item.order;

        const ourAllocations = (item.our_allocations || []).map((our) => ({
          id: our.id,
          order: Number(our.order?.id ?? our.order),
          amount: String(our.amount),
          date: our.date || null,
          comment: our.comment || "",
        }));

        if (Number(item.id) !== Number(selectedAllocation.id)) {
          return {
            id: item.id,
            order: Number(orderId),
            amount: String(item.amount),
            date: item.date || null,
            payment_agent: item.payment_agent || "",
            payment_status: item.payment_status || "",
            comment: item.comment || "",
            our_allocations: ourAllocations,
          };
        }

        return {
          id: item.id,
          order: Number(clientEditForm.order),
          amount: amount.toFixed(2),
          date: clientEditForm.date || null,
          payment_agent: clientEditForm.paymentAgent || "",
          payment_status: clientEditForm.paymentStatus || "",
          comment: clientEditForm.comment || "",
          our_allocations: ourAllocations,
        };
      });

      const saveResponse = await authFetch(detailUrl, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ allocations: updatedAllocations }),
      });

      if (!saveResponse.ok) {
        throw new Error(
          await getErrorMessage(
            saveResponse,
            "Не удалось сохранить клиентский платёж"
          )
        );
      }

      if (clientEditFiles.length > 0) {
        const formData = new FormData();

        clientEditFiles.forEach((file) => formData.append("files", file));

        const documentsResponse = await authFetch(
          `${BASE_URL}/api/accounting/allocations/${allocation.id}/documents/`,
          { method: "POST", body: formData }
        );

        if (!documentsResponse.ok) {
          throw new Error(
            await getErrorMessage(
              documentsResponse,
              "Платёж сохранён, но не удалось загрузить документы"
            )
          );
        }
      }

      setClientEditOpen(false);
      setClientEditPayment(null);
      setClientEditFiles([]);

      await loadAccountingPayments();
    } catch (error) {
      console.error(error);
      setClientEditError(
        error.message || "Не удалось сохранить клиентский платёж"
      );
    } finally {
      setSaving(false);
    }
  };

  // ---------------------------------------------------------
  // OUR EDIT
  // ---------------------------------------------------------

  const editOurPayment = async (payment) => {
    if (payment.type !== "our") return;
    setSaving(true);
    setOurEditError("");
    try {
      const response = await authFetch(`${ACCOUNTING_API}${payment.recordId}/`);
      if (!response.ok) {
        throw new Error(
          await getErrorMessage(response, "Не удалось загрузить платёж")
        );
      }
      const record = await response.json();
      const allocation = (record.allocations || []).find(
        (item) => Number(item.id) === Number(payment.allocationId)
      );
      if (!allocation) {
        throw new Error("Клиентское распределение не найдено");
      }
      const ourAllocation = (allocation.our_allocations || []).find(
        (item) => Number(item.id) === Number(payment.ourAllocationId)
      );
      if (!ourAllocation) {
        throw new Error("Наше распределение не найдено");
      }

      setOurEditPayment({ ...payment, record, allocation, ourAllocation });
      setOurEditForm({
        order: String(ourAllocation.order?.id ?? ourAllocation.order ?? ""),
        amount: String(ourAllocation.amount ?? ""),
        date: ourAllocation.date || record.date || "",
        paymentAgent: ourAllocation.payment_agent || "",
        paymentStatus: ourAllocation.payment_status || "",
        comment: ourAllocation.comment || "",
      });
      setOurEditFiles([]);
      setOurEditOpen(true);
    } catch (error) {
      console.error(error);
      alert(error.message || "Не удалось открыть редактирование");
    } finally {
      setSaving(false);
    }
  };

  const saveOurAccountingPayment = async (event) => {
    event.preventDefault();
    if (!ourEditPayment) return;

    const amount = Number(ourEditForm.amount);
    if (!Number.isFinite(amount) || amount <= 0) {
      setOurEditError("Сумма должна быть больше нуля");
      return;
    }

    setSaving(true);
    setOurEditError("");

    try {
      const { record, allocation, ourAllocation } = ourEditPayment;
      const detailUrl = `${ACCOUNTING_API}${record.id}/`;

      const detailResponse = await authFetch(detailUrl);

      if (!detailResponse.ok) {
        throw new Error(
          await getErrorMessage(detailResponse, "Не удалось загрузить запись")
        );
      }

      const latestRecord = await detailResponse.json();
      const allocations = Array.isArray(latestRecord.allocations)
        ? latestRecord.allocations
        : [];

      const selectedAllocation = allocations.find(
        (item) => Number(item.id) === Number(allocation.id)
      );

      if (!selectedAllocation) {
        throw new Error("Клиентское распределение больше не существует");
      }

      const updatedAllocations = allocations.map((item) => {
        const orderId = item.order?.id ?? item.order;

        const rebuiltOur = (item.our_allocations || []).map((our) => {
          const isTarget =
            Number(item.id) === Number(selectedAllocation.id) &&
            Number(our.id) === Number(ourAllocation.id);

          if (!isTarget) {
            return {
              id: our.id,
              order: Number(our.order?.id ?? our.order),
              amount: String(our.amount),
              date: our.date || null,
              payment_agent: our.payment_agent || "",
              payment_status: our.payment_status || "",
              comment: our.comment || "",
            };
          }

          return {
            id: our.id,
            order: Number(ourEditForm.order),
            amount: amount.toFixed(2),
            date: ourEditForm.date || null,
            payment_agent: ourEditForm.paymentAgent || "",
            payment_status: ourEditForm.paymentStatus || "",
            comment: ourEditForm.comment || "",
          };
        });

        return {
          id: item.id,
          order: Number(orderId),
          amount: String(item.amount),
          date: item.date || null,
          payment_agent: item.payment_agent || "",
          payment_status: item.payment_status || "",
          comment: item.comment || "",
          our_allocations: rebuiltOur,
        };
      });

      const saveResponse = await authFetch(detailUrl, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ allocations: updatedAllocations }),
      });

      if (!saveResponse.ok) {
        throw new Error(
          await getErrorMessage(saveResponse, "Не удалось сохранить наш платёж")
        );
      }

      if (ourEditFiles.length > 0) {
        const formData = new FormData();
        ourEditFiles.forEach((file) => formData.append("files", file));

        const documentsResponse = await authFetch(
          `${BASE_URL}/api/accounting/our-allocations/${ourAllocation.id}/documents/`,
          { method: "POST", body: formData }
        );

        if (!documentsResponse.ok) {
          throw new Error(
            await getErrorMessage(
              documentsResponse,
              "Платёж сохранён, но не удалось загрузить документы"
            )
          );
        }
      }

      setOurEditOpen(false);
      setOurEditPayment(null);
      setOurEditFiles([]);

      await loadAccountingPayments();
    } catch (error) {
      console.error(error);
      setOurEditError(error.message || "Не удалось сохранить наш платёж");
    } finally {
      setSaving(false);
    }
  };

  // ---------------------------------------------------------
  // DELETE ACCOUNTING PAYMENT
  // ---------------------------------------------------------

  const deleteAccountingPayment = async (payment) => {
    setSaving(true);

    try {
      const detailUrl = `${ACCOUNTING_API}${payment.recordId}/`;

      const detailResponse = await authFetch(detailUrl);

      if (!detailResponse.ok) {
        throw new Error(
          await getErrorMessage(
            detailResponse,
            "Не удалось загрузить запись Accounting"
          )
        );
      }

      const record = await detailResponse.json();
      const allocations = Array.isArray(record.allocations)
        ? record.allocations
        : [];

      let updatedAllocations;

      if (payment.type === "client") {
        updatedAllocations = allocations
          .filter(
            (allocation) =>
              Number(allocation.id) !== Number(payment.allocationId)
          )
          .map((allocation) => ({
            order: allocation.order?.id ?? allocation.order,
            amount: allocation.amount,
            our_allocations: (allocation.our_allocations || []).map((item) => ({
              order: item.order?.id ?? item.order,
              amount: item.amount,
              date: item.date,
              comment: item.comment || "",
            })),
          }));
      } else {
        updatedAllocations = allocations.map((allocation) => ({
          order: allocation.order?.id ?? allocation.order,
          amount: allocation.amount,
          our_allocations: (allocation.our_allocations || [])
            .filter(
              (item) =>
                !(
                  Number(allocation.id) === Number(payment.allocationId) &&
                  Number(item.id) === Number(payment.ourAllocationId)
                )
            )
            .map((item) => ({
              order: item.order?.id ?? item.order,
              amount: item.amount,
              date: item.date,
              comment: item.comment || "",
            })),
        }));
      }

      const response = await authFetch(detailUrl, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ allocations: updatedAllocations }),
      });

      if (!response.ok) {
        throw new Error(
          await getErrorMessage(response, "Не удалось удалить распределение")
        );
      }

      setAccountingPaymentToDelete(null);
      await loadAccountingPayments();
    } catch (error) {
      console.error(error);
      alert(error.message || "Не удалось удалить платеж");
    } finally {
      setSaving(false);
    }
  };

  // ---------------------------------------------------------
  // LOAD DATA
  // ---------------------------------------------------------

  const loadFactoryPayments = useCallback(async () => {
    let url = ORDER_FINANCES_API;

    if (id) {
      url += `?ordering=-id&order=${encodeURIComponent(id)}`;
    }

    const response = await authFetch(url);

    if (!response.ok) {
      throw new Error(
        await getErrorMessage(response, "Не удалось загрузить наши платежи")
      );
    }

    const data = await response.json();
    const rows = getList(data);

    setFinances(
      rows.filter(
        (item) =>
          item.factory_payment_date ||
          Number(item.factory_amount_cny || 0) !== 0 ||
          item.factory_payment_via ||
          item.factory_comment ||
          (item.proofs || []).some((proof) => proof.type === "factory")
      )
    );
  }, [id]);

  const loadAccountingPayments = useCallback(async () => {
    setAccountingError("");

    try {
      const response = await authFetch(ACCOUNTING_API);

      if (!response.ok) {
        throw new Error(
          await getErrorMessage(
            response,
            "Не удалось загрузить платежи из Accounting"
          )
        );
      }

      const data = await response.json();
      setAccountingRecords(getList(data));
    } catch (error) {
      console.error("Failed to load Accounting records:", error);
      setAccountingRecords([]);
      setAccountingError(
        error.message || "Не удалось загрузить платежи из Accounting"
      );
    }
  }, []);

  const loadSupplierIncomes = useCallback(async () => {
    setSupplierIncomesError("");

    if (!id) {
      setSupplierIncomes([]);
      return;
    }

    try {
      const url = `${SUPPLIER_PREPAYMENTS_API}?type=income&order=${encodeURIComponent(id)}`;
      const response = await authFetch(url);

      if (!response.ok) {
        throw new Error(
          await getErrorMessage(
            response,
            "Не удалось загрузить приходы от поставщиков"
          )
        );
      }

      const data = await response.json();
      setSupplierIncomes(getList(data));
    } catch (error) {
      console.error("Failed to load supplier incomes:", error);
      setSupplierIncomes([]);
      setSupplierIncomesError(
        error.message || "Не удалось загрузить приходы от поставщиков"
      );
    }
  }, [id]);

  const loadCommissions = useCallback(async () => {
    setCommissionsLoading(true);
    setCommissionsError("");

    if (!id) {
      setCommissionPayments([]);
      setCommissionsLoading(false);
      return;
    }

    try {
      const url = `${COMMISSION_ALLOCATIONS_API}?order=${encodeURIComponent(id)}`;
      const response = await authFetch(url);

      if (!response.ok) {
        throw new Error(
          await getErrorMessage(response, "Не удалось загрузить комиссии")
        );
      }

      const data = await response.json();
      setCommissionPayments(getList(data));
    } catch (error) {
      console.error("Failed to load commissions:", error);
      setCommissionPayments([]);
      setCommissionsError(error.message || "Не удалось загрузить комиссии");
    } finally {
      setCommissionsLoading(false);
    }
  }, [id]);


  const loadCompanyPayments = useCallback(async () => {
    setCompanyPaymentsLoading(true);
    setCompanyPaymentsError("");

    if (!id) {
      setCompanyPayments([]);
      setCompanyPaymentsLoading(false);
      return;
    }

    try {
      const url =
        `${PROFIT_ALLOCATIONS_API}` +
        `?order=${encodeURIComponent(id)}` +
        `&type=expense` +
        `&allocation_is_null=true`;

      const response = await authFetch(url);

      if (!response.ok) {
        throw new Error(
          await getErrorMessage(
            response,
            "Не удалось загрузить платежи со счёта компании"
          )
        );
      }

      setCompanyPayments(getList(await response.json()));
    } catch (err) {
      console.error("Failed to load company payments:", err);
      setCompanyPayments([]);
      setCompanyPaymentsError(
        err.message || "Не удалось загрузить платежи со счёта компании"
      );
    } finally {
      setCompanyPaymentsLoading(false);
    }
  }, [id]);



  const loadOrder = useCallback(async () => {
    if (!id) return;

    const response = await authFetch(`${ORDER_API_URL}${id}/`);

    if (!response.ok) {
      throw new Error(
        await getErrorMessage(response, "Не удалось загрузить заказ")
      );
    }

    setOrder(await response.json());
  }, [id]);

  const reloadData = useCallback(async () => {
    setLoading(true);

    try {
      const results = await Promise.allSettled([
        loadFactoryPayments(),
        loadAccountingPayments(),
        loadSupplierIncomes(),
        loadCommissions(),
        loadCompanyPayments(),
        loadOrder(),
      ]);

      const factoryResult = results[0];
      const orderResult = results[5];

      if (factoryResult.status === "rejected") {
        console.error(factoryResult.reason);
        alert(
          factoryResult.reason?.message ||
            "Не удалось загрузить платежи фабрике"
        );
      }

      if (orderResult.status === "rejected") {
        console.error(orderResult.reason);
      }
    } finally {
      setLoading(false);
    }
  }, [
    loadFactoryPayments,
    loadAccountingPayments,
    loadSupplierIncomes,
    loadCommissions,
    loadCompanyPayments,
    loadOrder,
  ]);

  useEffect(() => {
    reloadData();
  }, [reloadData]);

  // ---------------------------------------------------------
  // PAYMENT LISTS
  // ---------------------------------------------------------

  const clientPayments = accountingRecords.flatMap((record) => {
    const allocations = Array.isArray(record.allocations)
      ? record.allocations
      : [];

    return allocations
      .filter((allocation) => Number(allocation.order) === Number(id))
      .map((allocation) => ({
        id: `client-${record.id}-${allocation.id}`,
        recordId: record.id,
        allocationId: allocation.id,
        type: "client",
        source: "accounting",
        date: allocation.date || record.date,
        amount: allocation.amount,
        paymentAgent: allocation.payment_agent || "",
        recordTotal: record.money_sum,
        comment: allocation.comment ?? record.comment,
        paymentStatus: allocation.payment_status || "",
        clientName: record.client_name,
        documents: allocation.documents || record.documents || [],
      }));
  });

  const ourPayments = accountingRecords.flatMap((record) => {
    const allocations = Array.isArray(record.allocations)
      ? record.allocations
      : [];

    return allocations.flatMap((allocation) => {
      const ourAllocations = Array.isArray(allocation.our_allocations)
        ? allocation.our_allocations
        : [];

      return ourAllocations
        .filter(
          (ourAllocation) =>
            Number(ourAllocation.order?.id ?? ourAllocation.order) ===
            Number(id)
        )
        .map((ourAllocation) => ({
          id: `our-${record.id}-${allocation.id}-${ourAllocation.id}`,
          recordId: record.id,
          allocationId: allocation.id,
          ourAllocationId: ourAllocation.id,
          type: "our",
          source: "our-allocation",
          date: ourAllocation.date || record.date,
          amount: ourAllocation.amount,
          paymentAgent: ourAllocation.payment_agent || "",
          paymentStatus: ourAllocation.payment_status || "",
          comment: ourAllocation.comment || "",
          documents: Array.isArray(ourAllocation.documents)
            ? ourAllocation.documents
            : [],
        }));
    });
  });

  const supplierIncomePayments = supplierIncomes.map((income) => ({
    id: `supplier-income-${income.id}`,
    supplierPrepaymentId: income.id,
    supplierId: income.supplier,
    supplierName: income.supplier_name,
    orderCode: income.order_code,
    type: "supplier_income",
    source: "supplier-income",
    date: income.date,
    amount: income.amount,
    paymentAgent: income.payment_agent || "",
    paymentStatus: income.payment_status || "",
    comment: income.comment || "",
    documents: Array.isArray(income.documents) ? income.documents : [],
  }));


  const totalClientPaid = clientPayments.reduce(
    (sum, payment) => sum + Number(payment.amount || 0),
    0
  );

  // Старые «наши платежи фабрике» из /api/order-finances/
  const totalFactoryPaid = finances.reduce(
    (sum, item) => sum + Number(item.factory_amount_cny || 0),
    0
  );

  
  const totalCompanyPaid = companyPayments.reduce(
    (sum, p) => sum + Number(p.amount || 0),
    0
  );

  const totalWePaid =
    ourPayments.reduce(
      (sum, payment) => sum + Number(payment.amount || 0),
      0
    ) +
    supplierIncomePayments.reduce(
      (sum, payment) => sum + Number(payment.amount || 0),
      0
    ) +
    totalFactoryPaid +
    totalCompanyPaid;


  const clientRemaining =
    Number(order?.total_client_price || 0) - totalClientPaid;

  const weRemaining = Number(order?.total_internal_price || 0) - totalWePaid;


  // Единая хронологическая лента. Комиссии сюда НЕ включаем.
  const timeline = [
    ...clientPayments,
    ...ourPayments,
    ...supplierIncomePayments,
  ].sort((a, b) => {
    const dateA = a.date ? new Date(a.date).getTime() : 0;
    const dateB = b.date ? new Date(b.date).getTime() : 0;
    return dateB - dateA;
  });

  // ---------------------------------------------------------
  // FACTORY PAYMENT MODALS
  // ---------------------------------------------------------

  const openCreateModal = () => {
    setSelectedFinance(null);
    setFactoryForm({ ...EMPTY_FACTORY_FORM });
    setPaymentModalOpen(true);
  };

  const openEditModal = (finance) => {
    setSelectedFinance(finance);
    setFactoryForm({
      factory_payment_date: finance.factory_payment_date || "",
      factory_amount_cny: finance.factory_amount_cny ?? "",
      factory_payment_via: finance.factory_payment_via || "",
      factory_comment: finance.factory_comment || "",
      factory_proof: null,
    });
    setPaymentModalOpen(true);
  };

  const saveFactoryPayment = async (event) => {
    event.preventDefault();

    if (!id) {
      alert("Не найден ID заказа");
      return;
    }

    setSaving(true);

    try {
      const formData = new FormData();
      formData.append("order", id);

      FACTORY_FIELDS.forEach((field) => {
        const value = factoryForm[field];
        if (value !== undefined && value !== null) {
          formData.append(field, value);
        }
      });

      if (factoryForm.factory_proof instanceof File) {
        formData.append("factory_proof", factoryForm.factory_proof);
      }

      const isEdit = Boolean(selectedFinance);
      const url = isEdit
        ? `${ORDER_FINANCES_API}${selectedFinance.id}/`
        : ORDER_FINANCES_API;

      const response = await authFetch(url, {
        method: isEdit ? "PUT" : "POST",
        body: formData,
      });

      if (!response.ok) {
        throw new Error(
          await getErrorMessage(
            response,
            "Не удалось сохранить платеж фабрике"
          )
        );
      }

      const savedPayment = await response.json();

      setFinances((previous) => {
        if (isEdit) {
          return previous.map((item) =>
            item.id === savedPayment.id ? savedPayment : item
          );
        }
        return [
          savedPayment,
          ...previous.filter((item) => item.id !== savedPayment.id),
        ];
      });

      setPaymentModalOpen(false);
      setSelectedFinance(null);
      setFactoryForm({ ...EMPTY_FACTORY_FORM });
    } catch (error) {
      console.error(error);
      alert(error.message || "Не удалось сохранить платеж");
    } finally {
      setSaving(false);
    }
  };

  const deleteFactoryPayment = async (financeId) => {
    try {
      const response = await authFetch(
        `${ORDER_FINANCES_API}${financeId}/`,
        { method: "DELETE" }
      );

      if (!response.ok) {
        throw new Error(
          await getErrorMessage(response, "Не удалось удалить платеж")
        );
      }

      setFinances((previous) =>
        previous.filter((item) => item.id !== financeId)
      );

      setConfirmDeleteFinance(null);
    } catch (error) {
      console.error(error);
      alert(error.message || "Не удалось удалить платеж");
    }
  };


  const deleteCompanyPayment = async () => {
    if (!companyPaymentToDelete) return;

    setCompanyPaymentDeleting(true);
    setCompanyPaymentDeleteError("");

    try {
      const response = await authFetch(
        `${PROFIT_ALLOCATIONS_API}${companyPaymentToDelete.id}/`,
        { method: "DELETE" }
      );

      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || `Ошибка удаления: ${response.status}`);
      }

      setCompanyPaymentToDelete(null);
      await loadCompanyPayments();
    } catch (err) {
      setCompanyPaymentDeleteError(
        err.message || "Не удалось удалить платёж"
      );
    } finally {
      setCompanyPaymentDeleting(false);
    }
  };


  const deleteProof = async (proofId) => {
    if (!proofGalleryFinance) return;

    try {
      const response = await authFetch(
        `${ORDER_FINANCES_API}${proofGalleryFinance.id}/delete_proof/`,
        {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ id: proofId }),
        }
      );

      if (!response.ok) {
        throw new Error(
          await getErrorMessage(response, "Не удалось удалить подтверждение")
        );
      }

      const updateProofs = (finance) => ({
        ...finance,
        proofs: (finance.proofs || []).filter(
          (proof) => proof.id !== proofId
        ),
      });

      setFinances((previous) =>
        previous.map((finance) =>
          finance.id === proofGalleryFinance.id
            ? updateProofs(finance)
            : finance
        )
      );

      setProofGalleryFinance((previous) =>
        previous ? updateProofs(previous) : previous
      );
    } catch (error) {
      console.error(error);
      alert(error.message || "Не удалось удалить подтверждение");
    }
  };

  const handleProofsUploaded = (newProofs) => {
    setFinances((previous) =>
      previous.map((finance) =>
        finance.id === proofGalleryFinance?.id
          ? {
              ...finance,
              proofs: [...(finance.proofs || []), ...newProofs],
            }
          : finance
      )
    );

    setProofGalleryFinance((previous) =>
      previous
        ? {
            ...previous,
            proofs: [...(previous.proofs || []), ...newProofs],
          }
        : previous
    );
  };

  // ---------------------------------------------------------
  // EXCEL
  // ---------------------------------------------------------

  const downloadExcel = async (type) => {
    try {
      const response = await authFetch(
        `${ORDER_FINANCES_API}${type}-excel/?order=${encodeURIComponent(id)}`,
        { method: "GET" }
      );

      if (!response.ok) {
        throw new Error(
          await getErrorMessage(response, "Не удалось скачать Excel")
        );
      }

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = blobUrl;
      link.download = `factory_payments_${id}.xlsx`;

      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error(error);
      alert(error.message || "Не удалось скачать Excel");
    }
  };

  const downloadCommissionExcel = async (useCbr = false) => {
    try {
      const url =
        `${ORDER_FINANCES_API}comission-excel/` +
        `?order=${encodeURIComponent(id)}` +
        `&cb_rate=${useCbr ? "true" : "false"}`;

      const response = await authFetch(url, { method: "GET" });

      if (!response.ok) {
        throw new Error(
          await getErrorMessage(response, "Не удалось рассчитать комиссию")
        );
      }

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = blobUrl;
      link.download = `comission_${id}${useCbr ? "_cbr" : ""}.xlsx`;

      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error(error);
      alert(error.message || "Не удалось рассчитать комиссию");
    }
  };

  // ---------------------------------------------------------
  // INVOICES
  // ---------------------------------------------------------

  const downloadInvoice = async () => {
    try {
      const response = await authFetch(`${BASE_URL}/api/invoice/${id}/`);

      if (!response.ok) {
        throw new Error(
          await getErrorMessage(response, "Не удалось скачать инвойс")
        );
      }

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = blobUrl;
      link.download = `invoice_${id}.xlsx`;

      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error(error);
      alert(error.message || "Не удалось скачать инвойс");
    }
  };

  const downloadCompanyInvoice = async () => {
    try {
      const response = await authFetch(
        `${BASE_URL}/orders/${id}/company-invoice/download/`
      );

      if (!response.ok) {
        throw new Error(
          await getErrorMessage(response, "Не удалось скачать новый инвойс")
        );
      }

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = blobUrl;
      link.download = `company_invoice_${id}.xlsx`;

      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error(error);
      alert(error.message || "Не удалось скачать новый инвойс");
    }
  };

  // ---------------------------------------------------------
  // TRANSLATION CONTRACT
  // ---------------------------------------------------------

  const downloadTranslationContract = async () => {
    if (!order) {
      alert("Заказ ещё не загружен, попробуйте позже");
      return;
    }

    if (!order.client) {
      alert("Не найден ID клиента в заказе");
      return;
    }

    if (!contractDate) {
      alert("Пожалуйста, выберите дату");
      return;
    }

    setContractLoading(true);

    try {
      const url =
        `${BASE_URL}/api/contract-documents/translation-contracts/` +
        `translation-contracts/by-client/${order.client}/document/` +
        `?date=${encodeURIComponent(contractDate)}&order=${encodeURIComponent(id)}`;

      const response = await authFetch(url);

      if (!response.ok) {
        throw new Error(
          await getErrorMessage(response, "Не удалось скачать контракт")
        );
      }

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = blobUrl;
      link.download = `translation_contract_${id}.docx`;

      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);

      setShowContractModal(false);
      setContractDate("");
    } catch (error) {
      console.error(error);
      alert(error.message || "Не удалось скачать контракт");
    } finally {
      setContractLoading(false);
    }
  };

  // ---------------------------------------------------------
  // RENDER
  // ---------------------------------------------------------

  if (loading) {
    return <div className="p-4">{t("loading")}</div>;
  }

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-bold">{t("finance")}</h1>

      {order && (
        <OrderPaymentInfographic
          order={order}
          clientPaid={totalClientPaid}
          ourPaid={totalWePaid}
        />
      )}

      {order && (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
          <div>
            <p>
              <b>{t("client_price")}:</b> ¥
              {formatMoney(order.total_client_price)}
            </p>
            <p>
              <b>{t("price_for_us")}:</b> ¥
              {formatMoney(order.total_internal_price)}
            </p>
            <p>
              <b>{t("profit")}:</b> ¥{formatMoney(order.profit)}
            </p>
          </div>

          <div>
            <p>
              <b>
                Client production deposit amount{" "}
                {order.production_deposit_percent} %:
              </b>{" "}
              ¥{formatMoney(order.client_production_deposit_amount)}
            </p>
            <p>
              <b>
                Our production deposit amount{" "}
                {order.production_deposit_percent} %:
              </b>{" "}
              ¥{formatMoney(order.our_production_deposit_amount)}
            </p>
          </div>

          <div>
            <p>
              <b>{t("client_remaining")}:</b> ¥
              {formatMoney(clientRemaining)}
            </p>
            <p>
              <b>{t("our_remaining")}:</b> ¥{formatMoney(weRemaining)}
            </p>
          </div>

          <div>
            <p>
              <b>Клиент уже заплатил:</b> ¥{formatMoney(totalClientPaid)}
            </p>
            <p>
              <b>Мы уже заплатили:</b> ¥{formatMoney(totalWePaid)}
            </p>
          </div>
        </div>
      )}

      {accountingError && (
        <div className="rounded border border-red-300 bg-red-50 p-3 text-sm text-red-800">
          {accountingError}
          <button
            type="button"
            className="ml-2 underline"
            onClick={loadAccountingPayments}
          >
            Повторить загрузку
          </button>
        </div>
      )}

      {supplierIncomesError && (
        <div className="rounded border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800">
          {supplierIncomesError}
          <button
            type="button"
            className="ml-2 underline"
            onClick={loadSupplierIncomes}
          >
            Повторить загрузку
          </button>
        </div>
      )}

      {commissionsError && (
        <div className="rounded border border-blue-300 bg-blue-50 p-3 text-sm text-blue-800">
          {commissionsError}
          <button
            type="button"
            className="ml-2 underline"
            onClick={loadCommissions}
          >
            Повторить загрузку
          </button>
        </div>
      )}


      {companyPaymentsError && (
        <div className="rounded border border-emerald-300 bg-emerald-50 p-3 text-sm text-emerald-800">
          {companyPaymentsError}
          <button
            type="button"
            className="ml-2 underline"
            onClick={loadCompanyPayments}
          >
            Повторить загрузку
          </button>
        </div>
      )}


      <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">
            Платежи по заказу
          </h2>
          <p className="text-sm text-gray-500">
            Клиентские поступления, наши платежи и платежи фабрике.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
        
        <AddFactoryPaymentMenu
          onCreateFromAllocation={() => setOurFactoryModalOpen(true)}
          onCreateFromCompanyAccount={() => setCompanyAccountModalOpen(true)}
        />

          <button
            type="button"
            onClick={() => setClientAllocationModalOpen(true)}
            className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-blue-700"
          >
            <span className="text-lg leading-none">＋</span>
            Добавить платёж клиента
          </button>

          <ActionsMenu label="Ещё">
            <MenuItem onClick={() => downloadExcel("factory")}>
              📥 Скачать платежи поставщикам
            </MenuItem>

            <MenuItem onClick={() => downloadCommissionExcel(false)}>
              📊 Скачать комиссию
            </MenuItem>

            <MenuItem onClick={() => downloadCommissionExcel(true)}>
              📊 Скачать комиссию по курсу ЦБ
            </MenuItem>

            <MenuDivider />

            <MenuItem onClick={() => setShowContractModal(true)}>
              📄 Скачать контракт на перевод
            </MenuItem>

            <MenuItem onClick={downloadInvoice}>
              📄 Скачать инвойс
            </MenuItem>

            <MenuItem onClick={downloadCompanyInvoice}>
              📄 Скачать новый тип инвойса
            </MenuItem>

            <MenuDivider />

            <MenuItem onClick={reloadData}>🔄 Обновить платежи</MenuItem>
          </ActionsMenu>
        </div>
      </div>

      {/* ОДНА ОБЩАЯ ЛЕНТА ПЛАТЕЖЕЙ */}
      <div className="overflow-x-auto">
        <table className="w-full min-w-[950px] border-collapse text-sm">
          <thead>
            <tr className="border-b bg-gray-100 text-left">
              <th className="p-3">Тип платежа</th>
              <th className="p-3">Дата</th>
              <th className="p-3">Сумма</th>
              <th className="p-3">Платёж через</th>
              <th className="p-3">Комментарий</th>
              <th className="p-3">Статус платежа / документы</th>
              <th className="p-3">Действия</th>
            </tr>
          </thead>

          <tbody>
            {timeline.map((payment) => {
              const isClient = payment.source === "accounting";
              const isOur = payment.source === "our-allocation";
              const isSupplierIncome = payment.source === "supplier-income";

              if (isClient) {
                return (
                  <tr
                    key={payment.id}
                    onClick={() => openPaymentDetails(payment)}
                    title="Открыть детали платежа"
                    className="border-b bg-red-50 align-top cursor-pointer transition-colors duration-150 hover:bg-red-100"
                  >
                    <td className="p-3 font-semibold text-red-700">
                      Платёж клиента
                      <div className="mt-1 text-xs font-normal">
                        Accounting №{payment.recordId}
                      </div>
                    </td>

                    <td className="p-3">{formatDate(payment.date)}</td>

                    <td className="p-3 font-semibold text-red-700">
                      ¥{formatMoney(payment.amount)}
                      <div className="mt-1 text-xs font-normal text-gray-600">
                        Вся запись: {formatMoney(payment.recordTotal)}
                      </div>
                    </td>

                    <td className="p-3">{payment.paymentAgent || "—"}</td>

                    <td className="p-3 whitespace-pre-wrap">
                      {payment.comment || "—"}
                    </td>

                    <td className="p-3">
                      <div>{formatPaymentStatus(payment.paymentStatus)}</div>

                      {payment.documents.length > 0 ? (
                        <div className="mt-2 space-y-1">
                          {payment.documents.map((document) => {
                            const documentUrl = getDocumentUrl(document);
                            return documentUrl ? (
                              <div key={document.id}>
                                <a
                                  href={documentUrl}
                                  target="_blank"
                                  rel="noreferrer"
                                  onClick={(event) => event.stopPropagation()}
                                  className="text-blue-600 underline"
                                >
                                  {document.original_name ||
                                    `Документ ${document.id}`}
                                </a>
                              </div>
                            ) : null;
                          })}
                        </div>
                      ) : (
                        <div className="mt-1 text-xs text-gray-500">
                          Нет документов
                        </div>
                      )}
                    </td>

                    <td className="p-3">
                      <div className="flex flex-wrap gap-1">
                        <Button
                          className="px-2 py-1 text-xs"
                          variant="edit"
                          onClick={(event) => {
                            event.stopPropagation();
                            editAccountingPayment(payment);
                          }}
                        >
                          Редактировать
                        </Button>

                        <Button
                          className="px-2 py-1 text-xs"
                          variant="danger"
                          onClick={(event) => {
                            event.stopPropagation();
                            setAccountingPaymentToDelete(payment);
                          }}
                        >
                          Удалить
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              }

              if (isOur) {
                return (
                  <tr
                    key={payment.id}
                    onClick={() => openPaymentDetails(payment)}
                    title="Открыть детали платежа"
                    className="border-b bg-green-50 align-top cursor-pointer transition-colors duration-150 hover:bg-green-100"
                  >
                    <td className="p-3 font-semibold text-green-700">
                      Наш платёж
                      <div className="mt-1 text-xs font-normal">
                        Accounting №{payment.recordId}
                      </div>
                    </td>

                    <td className="p-3">{formatDate(payment.date)}</td>

                    <td className="p-3 font-semibold text-green-700">
                      ¥{formatMoney(payment.amount)}
                    </td>

                    <td className="p-3">{payment.paymentAgent || "—"}</td>

                    <td className="p-3 whitespace-pre-wrap">
                      {payment.comment || "—"}
                    </td>

                    <td className="p-3">
                      <div className="mb-2">
                        {formatPaymentStatus(payment.paymentStatus)}
                      </div>

                      {payment.documents.length > 0 ? (
                        <div className="space-y-1">
                          {payment.documents.map((document) => {
                            const documentUrl = getDocumentUrl(document);
                            return documentUrl ? (
                              <div key={document.id}>
                                <a
                                  href={documentUrl}
                                  target="_blank"
                                  rel="noreferrer"
                                  onClick={(event) => event.stopPropagation()}
                                  className="text-green-700 underline"
                                >
                                  {document.original_name ||
                                    `Документ ${document.id}`}
                                </a>
                              </div>
                            ) : null;
                          })}
                        </div>
                      ) : (
                        <span className="text-gray-500">Нет документов</span>
                      )}
                    </td>

                    <td className="p-3">
                      <div className="flex flex-wrap gap-1">
                        <Button
                          className="px-2 py-1 text-xs"
                          variant="edit"
                          onClick={(event) => {
                            event.stopPropagation();
                            editOurPayment(payment);
                          }}
                        >
                          Редактировать
                        </Button>

                        <Button
                          className="px-2 py-1 text-xs"
                          variant="danger"
                          onClick={(event) => {
                            event.stopPropagation();
                            setAccountingPaymentToDelete(payment);
                          }}
                        >
                          Удалить
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              }

              if (isSupplierIncome) {
                return (
                  <tr
                    key={payment.id}
                    onClick={() => openPaymentDetails(payment)}
                    title="Открыть счёт поставщика"
                    className="border-b bg-green-50 align-top cursor-pointer transition-colors duration-150 hover:bg-green-100"
                  >
                    <td className="p-3 font-semibold text-green-700">
                      Возврат от поставщика
                      <div className="mt-1 text-xs font-normal">
                        {payment.supplierName || "—"}
                      </div>
                    </td>

                    <td className="p-3">{formatDate(payment.date)}</td>

                    <td className="p-3 font-semibold text-green-700">
                      ¥{formatMoney(payment.amount)}
                      {payment.orderCode && (
                        <div className="mt-1 text-xs font-normal text-gray-600">
                          Заказ: {payment.orderCode}
                        </div>
                      )}
                    </td>

                    <td className="p-3">{payment.paymentAgent || "—"}</td>

                    <td className="p-3 whitespace-pre-wrap">
                      {payment.comment || "—"}
                    </td>

                    <td className="p-3">
                      <div>{formatPaymentStatus(payment.paymentStatus)}</div>

                      {payment.documents.length > 0 ? (
                        <div className="mt-2 space-y-1">
                          {payment.documents.map((document) => {
                            const documentUrl = getDocumentUrl(document);
                            return documentUrl ? (
                              <div key={document.id}>
                                <a
                                  href={documentUrl}
                                  target="_blank"
                                  rel="noreferrer"
                                  onClick={(event) => event.stopPropagation()}
                                  className="text-green-700 underline"
                                >
                                  {document.original_name ||
                                    `Документ ${document.id}`}
                                </a>
                              </div>
                            ) : null;
                          })}
                        </div>
                      ) : (
                        <span className="text-gray-500">Нет документов</span>
                      )}
                    </td>

                    <td className="p-3">
                      <div className="flex flex-wrap gap-1">
                        <Button
                          className="px-2 py-1 text-xs"
                          variant="edit"
                          onClick={(event) => {
                            event.stopPropagation();
                            navigate(
                              `/accounting/supplier-accounts/${payment.supplierId}`
                            );
                          }}
                        >
                          Открыть счёт поставщика
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              }

              return null;
            })}

            {/* ПЛАТЕЖИ ФАБРИКЕ */}
            {finances.map((finance) => (
              <tr
                key={`factory-${finance.id}`}
                onClick={() => openEditModal(finance)}
                title="Открыть платёж фабрике"
                className="border-b bg-green-50 align-top cursor-pointer transition-colors duration-150 hover:bg-green-100"
              >
                <td className="p-3 font-semibold text-green-700">
                  Наш платёж фабрике
                </td>

                <td className="p-3">
                  {formatDate(finance.factory_payment_date)}
                </td>

                <td className="p-3 font-semibold text-green-700">
                  ¥{formatMoney(finance.factory_amount_cny)}
                </td>

                <td className="p-3">
                  {finance.factory_payment_via || "—"}
                </td>

                <td className="p-3 whitespace-pre-wrap">
                  {finance.factory_comment || "—"}
                </td>

                <td className="p-3">
                  {(finance.proofs || []).some(
                    (proof) => proof.type === "factory"
                  ) ? (
                    <button
                      type="button"
                      className="text-green-700 underline"
                      onClick={(event) => {
                        event.stopPropagation();
                        setProofGalleryFinance(finance);
                        setProofType("factory");
                      }}
                    >
                      Открыть подтверждения
                    </button>
                  ) : (
                    <span className="text-gray-500">Нет подтверждений</span>
                  )}
                </td>

                <td className="p-3">
                  <div className="flex flex-wrap gap-1">
                    <Button
                      className="px-2 py-1 text-xs"
                      variant="edit"
                      onClick={(event) => {
                        event.stopPropagation();
                        openEditModal(finance);
                      }}
                    >
                      {t("edit")}
                    </Button>

                    <Button
                      className="px-2 py-1 text-xs"
                      variant="danger"
                      onClick={(event) => {
                        event.stopPropagation();
                        setConfirmDeleteFinance(finance);
                      }}
                    >
                      {t("delete")}
                    </Button>
                  </div>
                </td>
              </tr>
            ))}


              {companyPayments.map((payment) => (
                <tr
                  key={`company-account-${payment.id}`}
                  className="border-b bg-emerald-50 align-top transition-colors duration-150 hover:bg-emerald-100"
                >
                  <td className="p-3 font-semibold text-emerald-800">
                    Наш платёж со счёта компании
                    {payment.account_name && (
                      <div className="mt-1 text-xs font-normal text-gray-600">
                        Счёт: {payment.account_name}
                      </div>
                    )}
                    {payment.order_code && (
                      <div className="mt-1 text-xs font-normal text-gray-600">
                        Заказ: {payment.order_code}
                      </div>
                    )}
                  </td>
                  <td className="p-3">{formatDate(payment.date)}</td>
                  <td className="p-3 font-semibold text-emerald-800">
                    {payment.currency_symbol || "¥"}
                    {formatMoney(payment.amount)}
                  </td>
                  <td className="p-3">{payment.payment_agent || "—"}</td>
                  <td className="p-3 whitespace-pre-wrap">{payment.comment || "—"}</td>
                  <td className="p-3">
                    <div>{formatPaymentStatus(payment.payment_status)}</div>
                    {payment.documents?.length > 0 ? (
                      <div className="mt-2 space-y-1">
                        {payment.documents.map((doc) => {
                          const url = getDocumentUrl(doc);
                          return url ? (
                            <div key={doc.id}>
                              <a
                                href={url}
                                target="_blank"
                                rel="noreferrer"
                                className="text-emerald-700 underline"
                              >
                                {doc.original_name || `Документ ${doc.id}`}
                              </a>
                            </div>
                          ) : null;
                        })}
                      </div>
                    ) : (
                      <span className="text-gray-500">Нет документов</span>
                    )}
                  </td>
                 <td className="p-3">
                  <div className="flex flex-wrap gap-1">
                    <Button
                      className="px-2 py-1 text-xs"
                      variant="edit"
                      onClick={(event) => {
                        event.stopPropagation();
                        setCompanyPaymentToEdit(payment);
                      }}
                    >
                      Редактировать
                    </Button>

                    <Button
                      className="px-2 py-1 text-xs"
                      variant="danger"
                      onClick={(event) => {
                        event.stopPropagation();
                        setCompanyPaymentToDelete(payment);
                      }}
                    >
                      Удалить
                    </Button>
                  </div>
                </td>
                </tr>
              ))}

              {companyPaymentsLoading && (
                <tr>
                  <td colSpan={7} className="p-3 text-center text-sm text-gray-500">
                    Загрузка платежей со счёта компании…
                  </td>
                </tr>
              )}


            {/* КОМИССИОННЫЕ ВОЗНАГРАЖДЕНИЯ */}
            {commissionPayments.map((commission) => (
              <CommissionPaymentRow
                key={`commission-${commission.id}`}
                commission={commission}
                onOpenPayment={(paymentId) =>
                  navigate(`/accounting/${paymentId}/`)
                }
              />
            ))}

            {commissionsLoading && (
              <tr>
                <td colSpan={7} className="p-3 text-center text-sm text-gray-500">
                  Загрузка комиссий…
                </td>
              </tr>
            )}
            {timeline.length === 0 &&
              finances.length === 0 &&
              commissionPayments.length === 0 &&
              companyPayments.length === 0 &&
              !commissionsLoading && (
                <tr>
                  <td colSpan={7} className="p-6 text-center text-gray-500">
                    Платежей по этому заказу пока нет.
                  </td>
                </tr>
              )}
          </tbody>
        </table>
      </div>

      {/* МОДАЛКА УДАЛЕНИЯ РАСПРЕДЕЛЕНИЯ */}
      {accountingPaymentToDelete && (
        <Modal
          isOpen={true}
          onClose={() => {
            if (!saving) setAccountingPaymentToDelete(null);
          }}
        >
          <div className="space-y-4 p-4">
            <h2 className="text-lg font-bold">Удалить распределение?</h2>

            <p>
              {accountingPaymentToDelete.type === "client"
                ? "Будет удалено клиентское распределение платежа на этот заказ."
                : "Будет удалено наше распределение платежа на этот заказ."}
            </p>

            <p className="font-semibold">
              ¥{formatMoney(accountingPaymentToDelete.amount)}
            </p>

            <div className="flex justify-end gap-2">
              <Button
                variant="secondary"
                disabled={saving}
                onClick={() => setAccountingPaymentToDelete(null)}
              >
                Отмена
              </Button>

              <Button
                variant="danger"
                disabled={saving}
                onClick={() =>
                  deleteAccountingPayment(accountingPaymentToDelete)
                }
              >
                {saving ? "Удаление..." : "Удалить"}
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* РЕДАКТИРОВАНИЕ КЛИЕНТСКОГО ПЛАТЕЖА */}
      {clientEditOpen && clientEditPayment && (
        <Modal
          isOpen={true}
          onClose={() => {
            if (!saving) {
              setClientEditOpen(false);
              setClientEditPayment(null);
              setClientEditFiles([]);
              setClientEditError("");
            }
          }}
        >
          <form
            onSubmit={saveClientAccountingPayment}
            className="min-w-[300px] space-y-4 p-4"
          >
            <h2 className="text-xl font-bold">Редактировать платёж клиента</h2>

            <div>
              <label className="mb-1 block text-sm font-medium">Заказ</label>
              <input
                type="text"
                value={clientEditForm.order}
                onChange={(event) =>
                  setClientEditForm((previous) => ({
                    ...previous,
                    order: event.target.value,
                  }))
                }
                required
                className="w-full rounded border px-3 py-2"
              />
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium">Сумма</label>
              <input
                type="number"
                step="0.01"
                min="0.01"
                value={clientEditForm.amount}
                onChange={(event) =>
                  setClientEditForm((previous) => ({
                    ...previous,
                    amount: event.target.value,
                  }))
                }
                required
                className="w-full rounded border px-3 py-2"
              />
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium">
                Дата платежа
              </label>
              <input
                type="date"
                value={clientEditForm.date}
                onChange={(event) =>
                  setClientEditForm((previous) => ({
                    ...previous,
                    date: event.target.value,
                  }))
                }
                className="w-full rounded border px-3 py-2"
              />
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium">
                Платёж через
              </label>
              <input
                type="text"
                value={clientEditForm.paymentAgent}
                onChange={(event) =>
                  setClientEditForm((previous) => ({
                    ...previous,
                    paymentAgent: event.target.value,
                  }))
                }
                className="w-full rounded border px-3 py-2"
              />
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium">
                Статус платежа
              </label>

              <select
                value={clientEditForm.paymentStatus}
                onChange={(event) =>
                  setClientEditForm((previous) => ({
                    ...previous,
                    paymentStatus: event.target.value,
                  }))
                }
                className="w-full rounded border px-3 py-2"
              >
                <option value="">Выберите статус</option>

                {PAYMENT_STATUS_OPTIONS.map((status) => (
                  <option key={status.value} value={status.value}>
                    {status.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium">
                Комментарий
              </label>
              <textarea
                rows={3}
                value={clientEditForm.comment}
                onChange={(event) =>
                  setClientEditForm((previous) => ({
                    ...previous,
                    comment: event.target.value,
                  }))
                }
                className="w-full rounded border px-3 py-2"
              />
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium">
                Добавить документы
              </label>
              <input
                type="file"
                multiple
                onChange={(event) =>
                  setClientEditFiles(Array.from(event.target.files || []))
                }
                className="w-full text-sm"
              />

              {clientEditPayment.allocation.documents?.length > 0 && (
                <div className="mt-2 space-y-1">
                  <p className="text-sm font-medium">
                    Прикреплённые документы
                  </p>

                  {clientEditPayment.allocation.documents.map((document) => {
                    const documentUrl = getDocumentUrl(document);
                    return documentUrl ? (
                      <div key={document.id}>
                        <a
                          href={documentUrl}
                          target="_blank"
                          rel="noreferrer"
                          onClick={(event) => event.stopPropagation()}
                          className="text-blue-600 underline"
                        >
                          {document.original_name || `Документ ${document.id}`}
                        </a>
                      </div>
                    ) : null;
                  })}
                </div>
              )}
            </div>

            {clientEditError && (
              <div className="rounded border border-red-300 bg-red-50 p-3 text-sm text-red-700">
                {clientEditError}
              </div>
            )}

            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="secondary"
                disabled={saving}
                onClick={() => {
                  setClientEditOpen(false);
                  setClientEditPayment(null);
                  setClientEditFiles([]);
                  setClientEditError("");
                }}
              >
                Отмена
              </Button>

              <Button type="submit" variant="primary" disabled={saving}>
                {saving ? "Сохранение..." : "Сохранить"}
              </Button>
            </div>
          </form>
        </Modal>
      )}

      {/* РЕДАКТИРОВАНИЕ НАШЕГО ПЛАТЕЖА */}
      {ourEditOpen && ourEditPayment && (
        <Modal
          isOpen={true}
          onClose={() => {
            if (!saving) {
              setOurEditOpen(false);
              setOurEditPayment(null);
              setOurEditFiles([]);
              setOurEditError("");
            }
          }}
        >
          <form
            onSubmit={saveOurAccountingPayment}
            className="min-w-[300px] space-y-4 p-4"
          >
            <h2 className="text-xl font-bold">Редактировать наш платёж</h2>

            <div>
              <label className="mb-1 block text-sm font-medium">Заказ</label>
              <input
                type="text"
                value={ourEditForm.order}
                onChange={(event) =>
                  setOurEditForm((previous) => ({
                    ...previous,
                    order: event.target.value,
                  }))
                }
                required
                className="w-full rounded border px-3 py-2"
              />
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium">Сумма</label>
              <input
                type="number"
                step="0.01"
                min="0.01"
                value={ourEditForm.amount}
                onChange={(event) =>
                  setOurEditForm((previous) => ({
                    ...previous,
                    amount: event.target.value,
                  }))
                }
                required
                className="w-full rounded border px-3 py-2"
              />
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium">
                Дата платежа
              </label>
              <input
                type="date"
                value={ourEditForm.date}
                onChange={(event) =>
                  setOurEditForm((previous) => ({
                    ...previous,
                    date: event.target.value,
                  }))
                }
                className="w-full rounded border px-3 py-2"
              />
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium">
                Платёж через
              </label>
              <input
                type="text"
                value={ourEditForm.paymentAgent}
                onChange={(event) =>
                  setOurEditForm((previous) => ({
                    ...previous,
                    paymentAgent: event.target.value,
                  }))
                }
                className="w-full rounded border px-3 py-2"
              />
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium">
                Статус платежа
              </label>

              <select
                value={ourEditForm.paymentStatus}
                onChange={(event) =>
                  setOurEditForm((previous) => ({
                    ...previous,
                    paymentStatus: event.target.value,
                  }))
                }
                className="w-full rounded border px-3 py-2"
              >
                <option value="">Выберите статус</option>

                {PAYMENT_STATUS_OPTIONS.map((status) => (
                  <option key={status.value} value={status.value}>
                    {status.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium">
                Комментарий
              </label>
              <textarea
                rows={3}
                value={ourEditForm.comment}
                onChange={(event) =>
                  setOurEditForm((previous) => ({
                    ...previous,
                    comment: event.target.value,
                  }))
                }
                className="w-full rounded border px-3 py-2"
              />
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium">
                Добавить документы
              </label>
              <input
                type="file"
                multiple
                onChange={(event) =>
                  setOurEditFiles(Array.from(event.target.files || []))
                }
                className="w-full text-sm"
              />
              {ourEditPayment.ourAllocation.documents?.length > 0 && (
                <div className="mt-2 space-y-1">
                  <p className="text-sm font-medium">
                    Прикреплённые документы
                  </p>
                  {ourEditPayment.ourAllocation.documents.map((document) => {
                    const documentUrl = getDocumentUrl(document);
                    return documentUrl ? (
                      <div key={document.id}>
                        <a
                          href={documentUrl}
                          target="_blank"
                          rel="noreferrer"
                          onClick={(event) => event.stopPropagation()}
                          className="text-blue-600 underline"
                        >
                          {document.original_name || `Документ ${document.id}`}
                        </a>
                      </div>
                    ) : null;
                  })}
                </div>
              )}
            </div>

            {ourEditError && (
              <div className="rounded border border-red-300 bg-red-50 p-3 text-sm text-red-700">
                {ourEditError}
              </div>
            )}

            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="secondary"
                disabled={saving}
                onClick={() => {
                  setOurEditOpen(false);
                  setOurEditPayment(null);
                  setOurEditFiles([]);
                  setOurEditError("");
                }}
              >
                Отмена
              </Button>
              <Button type="submit" variant="primary" disabled={saving}>
                {saving ? "Сохранение..." : "Сохранить"}
              </Button>
            </div>
          </form>
        </Modal>
      )}

      {/* СОЗДАНИЕ / РЕДАКТИРОВАНИЕ ПЛАТЕЖА ФАБРИКЕ */}
      {paymentModalOpen && (
        <Modal
          isOpen={true}
          onClose={() => {
            if (!saving) setPaymentModalOpen(false);
          }}
        >
          <form
            onSubmit={saveFactoryPayment}
            className="min-w-[300px] space-y-4 p-4"
          >
            <h2 className="text-xl font-bold">
              {selectedFinance
                ? "Редактировать наш платёж фабрике"
                : "Новый платёж фабрике"}
            </h2>

            <div>
              <label className="mb-1 block text-sm font-medium">
                Дата платежа
              </label>
              <input
                type="date"
                value={factoryForm.factory_payment_date}
                onChange={(event) =>
                  setFactoryForm((previous) => ({
                    ...previous,
                    factory_payment_date: event.target.value,
                  }))
                }
                className="w-full rounded border px-3 py-2"
              />
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium">
                Сумма в юанях
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                required
                value={factoryForm.factory_amount_cny}
                onChange={(event) =>
                  setFactoryForm((previous) => ({
                    ...previous,
                    factory_amount_cny: event.target.value,
                  }))
                }
                className="w-full rounded border px-3 py-2"
              />
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium">
                Способ оплаты
              </label>
              <input
                type="text"
                value={factoryForm.factory_payment_via}
                onChange={(event) =>
                  setFactoryForm((previous) => ({
                    ...previous,
                    factory_payment_via: event.target.value,
                  }))
                }
                className="w-full rounded border px-3 py-2"
              />
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium">
                Комментарий
              </label>
              <textarea
                value={factoryForm.factory_comment}
                onChange={(event) =>
                  setFactoryForm((previous) => ({
                    ...previous,
                    factory_comment: event.target.value,
                  }))
                }
                rows={3}
                className="w-full rounded border px-3 py-2"
              />
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium">
                Подтверждение платежа
              </label>
              <input
                type="file"
                onChange={(event) =>
                  setFactoryForm((previous) => ({
                    ...previous,
                    factory_proof: event.target.files?.[0] || null,
                  }))
                }
                className="w-full text-sm"
              />
            </div>

            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="secondary"
                onClick={() => setPaymentModalOpen(false)}
                disabled={saving}
              >
                Отмена
              </Button>

              <Button type="submit" variant="primary" disabled={saving}>
                {saving ? "Сохранение..." : "Сохранить"}
              </Button>
            </div>
          </form>
        </Modal>
      )}

      {/* ПОДТВЕРЖДЕНИЯ ПЛАТЕЖА ФАБРИКЕ */}
      {proofGalleryFinance && (
        <Modal isOpen={true} onClose={() => setProofGalleryFinance(null)}>
          <div className="space-y-4 p-4">
            <div className="flex items-center justify-between gap-2">
              <h2 className="text-lg font-semibold">
                Подтверждения платежа фабрике
              </h2>
              <Button onClick={() => setProofGalleryFinance(null)}>
                Закрыть
              </Button>
            </div>

            <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
              {(proofGalleryFinance.proofs || [])
                .filter((proof) => proof.type === "factory")
                .map((proof) => (
                  <div
                    key={proof.id}
                    className="relative overflow-hidden rounded border"
                  >
                    <img
                      src={proof.image}
                      alt="Подтверждение платежа"
                      className="h-28 w-full cursor-pointer object-cover"
                      onClick={() => setPreviewProof(proof)}
                    />

                    <button
                      type="button"
                      className="absolute right-1 top-1 rounded-full bg-white px-2 text-red-600"
                      onClick={() => setConfirmDeleteProof(proof)}
                    >
                      ×
                    </button>
                  </div>
                ))}
            </div>

            <OrderFinanceProofUpload
              financeId={proofGalleryFinance.id}
              type="factory"
              onUploaded={handleProofsUploaded}
            />
          </div>
        </Modal>
      )}

      {/* УДАЛЕНИЕ ПЛАТЕЖА ФАБРИКЕ */}
      {confirmDeleteFinance && (
        <Modal isOpen={true} onClose={() => setConfirmDeleteFinance(null)}>
          <div className="space-y-4 p-4">
            <p>Удалить этот платёж фабрике?</p>
            <p className="font-semibold">
              ¥{formatMoney(confirmDeleteFinance.factory_amount_cny)}
            </p>

            <div className="flex justify-end gap-2">
              <Button onClick={() => setConfirmDeleteFinance(null)}>
                Отмена
              </Button>
              <Button
                variant="danger"
                onClick={() => deleteFactoryPayment(confirmDeleteFinance.id)}
              >
                Удалить
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {companyPaymentToDelete && (
        <Modal
          isOpen={true}
          onClose={() => {
            if (!companyPaymentDeleting) {
              setCompanyPaymentToDelete(null);
              setCompanyPaymentDeleteError("");
            }
          }}
        >
          <div className="space-y-4 p-4">
            <h2 className="text-lg font-bold">Удалить платёж со счёта компании?</h2>

            <p className="text-sm text-gray-700">
              Заказ:{" "}
              <strong>
                {companyPaymentToDelete.order_code ||
                  `#${companyPaymentToDelete.order}`}
              </strong>
              <br />
              Счёт: <strong>{companyPaymentToDelete.account_name || "—"}</strong>
              <br />
              Сумма:{" "}
              <strong>
                {companyPaymentToDelete.currency_symbol || "¥"}
                {formatMoney(companyPaymentToDelete.amount)}
              </strong>
            </p>

            {companyPaymentDeleteError && (
              <div className="whitespace-pre-wrap break-words rounded border border-red-300 bg-red-50 p-3 text-sm text-red-700">
                {companyPaymentDeleteError}
              </div>
            )}

            <div className="flex justify-end gap-2">
              <Button
                variant="secondary"
                disabled={companyPaymentDeleting}
                onClick={() => {
                  setCompanyPaymentToDelete(null);
                  setCompanyPaymentDeleteError("");
                }}
              >
                Отмена
              </Button>
              <Button
                variant="danger"
                disabled={companyPaymentDeleting}
                onClick={deleteCompanyPayment}
              >
                {companyPaymentDeleting ? "Удаление..." : "Удалить"}
              </Button>
            </div>
          </div>
        </Modal>
      )}


      {/* УДАЛЕНИЕ ПОДТВЕРЖДЕНИЯ */}
      {confirmDeleteProof && (
        <Modal isOpen={true} onClose={() => setConfirmDeleteProof(null)}>
          <div className="space-y-3 p-4">
            <p>Удалить это подтверждение?</p>

            <img
              src={confirmDeleteProof.image}
              alt="Подтверждение платежа"
              className="h-40 w-full object-contain"
            />

            <div className="flex justify-end gap-2">
              <Button onClick={() => setConfirmDeleteProof(null)}>
                Отмена
              </Button>
              <Button
                variant="danger"
                onClick={async () => {
                  await deleteProof(confirmDeleteProof.id);
                  setConfirmDeleteProof(null);
                }}
              >
                Удалить
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* ПРОСМОТР ПОДТВЕРЖДЕНИЯ */}
      {previewProof && (
        <Modal isOpen={true} onClose={() => setPreviewProof(null)}>
          <div className="space-y-3 p-3">
            <img
              src={previewProof.image}
              alt="Просмотр подтверждения"
              className="max-h-[80vh] w-full object-contain"
            />

            <p className="text-sm text-gray-600">
              {previewProof.comment || "Без комментария"}
            </p>

            <Button onClick={() => setPreviewProof(null)}>Закрыть</Button>
          </div>
        </Modal>
      )}

      {/* ДАТА КОНТРАКТА */}
      {showContractModal && (
        <Modal
          isOpen={true}
          onClose={() => {
            setShowContractModal(false);
            setContractDate("");
          }}
        >
          <div className="min-w-[300px] space-y-4 p-4">
            <h2 className="text-xl font-bold">Выберите дату контракта</h2>

            <div>
              <label className="mb-1 block text-sm font-medium">
                Дата договора
              </label>
              <input
                type="date"
                value={contractDate}
                onChange={(event) => setContractDate(event.target.value)}
                className="w-full rounded border px-3 py-2"
              />
            </div>

            <div className="flex justify-end gap-2">
              <Button
                variant="secondary"
                onClick={() => {
                  setShowContractModal(false);
                  setContractDate("");
                }}
              >
                Отмена
              </Button>

              <Button
                variant="primary"
                onClick={downloadTranslationContract}
                disabled={!contractDate || contractLoading}
              >
                {contractLoading ? "Загрузка..." : "Скачать"}
              </Button>
            </div>
          </div>
        </Modal>
      )}

      <ClientPaymentAllocationModal
        isOpen={clientAllocationModalOpen}
        onClose={() => setClientAllocationModalOpen(false)}
        orderId={id}
        clientId={order?.client?.id ?? order?.client ?? null}
        accountingRecords={accountingRecords}
        onSaved={loadAccountingPayments}
      />

      <OurPaymentToFactoryModal
        isOpen={ourFactoryModalOpen}
        onClose={() => setOurFactoryModalOpen(false)}
        orderId={id}
        accountingRecords={accountingRecords}
        onSaved={loadAccountingPayments}
      />

      {companyAccountModalOpen && (
        <CompanyAccountPaymentModal
          orderId={id}
          onClose={() => setCompanyAccountModalOpen(false)}
          onSaved={async () => {
            setCompanyAccountModalOpen(false);
            await loadCompanyPayments();
          }}
        />
      )}


      {companyPaymentToEdit && (
        <CompanyAccountPaymentModal
          orderId={id}
          payment={companyPaymentToEdit}
          onClose={() => setCompanyPaymentToEdit(null)}
          onSaved={async () => {
            setCompanyPaymentToEdit(null);
            await loadCompanyPayments();
          }}
        />
      )}


    </div>
  );
}